import React, { useContext, useState, useEffect } from 'react';
import { Container, Typography, TextField, Button, Box, MenuItem, Alert, Paper, Divider, Chip,
  InputAdornment,
  IconButton
 } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import CakeIcon from '@mui/icons-material/Cake';
import WcIcon from '@mui/icons-material/Wc';
import PhoneIcon from '@mui/icons-material/Phone';
import SaveIcon from '@mui/icons-material/Save';
import GoogleIcon from '@mui/icons-material/Google';
import VerifiedIcon from '@mui/icons-material/Verified';
import StorefrontIcon from '@mui/icons-material/Storefront';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import PhoneNumberField from '../components/PhoneNumberField';
import PhoneVerification from '../components/PhoneVerification';
import { useNavigate } from 'react-router-dom';
import {   
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const UserAccountPage = ({keywordsData, language}) => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    age: user?.age || '',
    sex: user?.sex || '',
    location: user?.location || '',
    user_phone: user?.user_phone || '',
    password: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showVerification, setShowVerification] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(user?.is_phone_verified || false);

  const handleChange = (field) => (eventOrValue) => {
    let value;
    
    if (typeof eventOrValue === 'object' && eventOrValue.target) {
      value = eventOrValue.target.value;
    } else {
      value = eventOrValue;
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  const validateField = (field, value) => {
    switch (field) {
      case 'username':
        if (!value) return language === 'ar' ? 'اسم المستخدم مطلوب' : 'Username is required';
        if (value.length < 3) return language === 'ar' ? 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل' : 'Username must be at least 3 characters';
        return '';
      case 'user_phone':
        if (!value) return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
        const cleanPhone = value.replace(/[\s\-\(\)\.]/g, '');
        const phoneRegex = /^(\+?[0-9]{1,3})?[0-9]{7,12}$/;
        if (!phoneRegex.test(cleanPhone)) {
          return language === 'ar' 
            ? 'الرجاء إدخال رقم هاتف صالح (مثال: 0123456789 أو +20123456789)' 
            : 'Please enter a valid phone number (e.g., 0123456789 or +20123456789)';
        }
        return '';
      case 'email':
        if (!value) return language === 'ar' ? 'الإيميل مطلوب' : 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return language === 'ar' ? 'يرجى إدخال عنوان بريد إلكتروني صالح' : 'Please enter a valid email address';
        return '';
      case 'newPassword':
        // Only validate if password is being changed (newPassword has value)
        if (value && value.length < 6) return language === 'ar' ? 'يجب أن تتكون كلمة المرور من 6 أحرف على الأقل' : 'Password must be at least 6 characters';
        return '';
      case 'confirmPassword':
        // Only validate if newPassword has value
        if (formData.newPassword && value !== formData.newPassword) return language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match';
        return '';
      case 'password':
        // Only validate if newPassword is being set (user is trying to change password)
        if (formData.newPassword && !value) return language === 'ar' ? 'كلمة المرور الحالية مطلوبة' : 'Current password is required';
        return '';
      case 'age':
        if (!value) return language === 'ar' ? 'العمر مطلوب' : 'Age is required';
        if (value && (value < 1 || value > 120)) return language === 'ar' ? 'الرجاء إدخال عمر صالح' : 'Please enter a valid age';
        return '';
      case 'sex':
        if (!value) return language === 'ar' ? 'نوع الجنس مطلوب' : 'Gender is required';
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newTouched = {};
    const newErrors = {};
    
    // Only validate required fields (username, email, age, sex, user_phone)
    const requiredFields = ['username', 'email', 'age', 'sex', 'user_phone'];
    
    requiredFields.forEach(field => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    
    // Validate password fields only if user is trying to change password
    if (formData.newPassword) {
      newTouched.password = true;
      newTouched.newPassword = true;
      newTouched.confirmPassword = true;
      newErrors.password = validateField('password', formData.password);
      newErrors.newPassword = validateField('newPassword', formData.newPassword);
      newErrors.confirmPassword = validateField('confirmPassword', formData.confirmPassword);
    }
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    if (Object.values(newErrors).some(error => error)) {
      setMessage({ type: 'error', text: language === 'ar' ? 'يرجى تصحيح الأخطاء الموجودة في النموذج' : 'Please correct the errors in the form' });
      return;
    }
    
    try {
      // Prepare data for submission - remove empty password fields if not changing password
      const submitData = { ...formData };
      if (!formData.newPassword) {
        delete submitData.password;
        delete submitData.newPassword;
        delete submitData.confirmPassword;
      }
      
      const response = await api.put('/users/update', submitData);
      updateUser(response.data.user);
      setMessage({ type: 'success', text: language === 'ar' ? 'تم تحديث الملف الشخصي بنجاح!' : 'Profile updated successfully!' });
      
      setFormData(prev => ({
        ...prev,
        password: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || (language === 'ar' ? 'فشل التحديث' : 'Update failed') });
    }
  };

  const handlePhoneVerified = () => {
    setIsPhoneVerified(true);
    setShowVerification(false);
    fetchUserData();
  };

  const fetchUserData = async () => {
    try {
      const response = await api.get('/users/me');
      updateUser(response.data.user);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleUpgradeToShopOwner = () => {
    navigate('/shop-owner-request');
  };

  return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      mb: 10,
      backgroundColor: '#000000',
      minHeight: '100vh',
      color: '#FFFFFF'
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <AccountCircleIcon sx={{ fontSize: 48, color: '#FFD700' }} />
        <Typography variant="h3" sx={{ 
          color: '#FFD700',
          fontWeight: 900,
          letterSpacing: '2px'
        }}>
          {language === 'ar' ? 'الحساب الشخصي' : 'Account'}
        </Typography>
      </Box>

      {/* Welcome Message */}
      <Paper sx={{ 
        p: 3, 
        mb: 4,
        backgroundColor: '#0D0D0D',
        border: '1px solid #FFD700',
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <AccountCircleIcon sx={{ fontSize: 40, color: '#FFD700' }} />
        <Box>
          <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
            {language === 'ar' ? 'مرحباً، ' : 'Welcome, '} {formData.username}!
          </Typography>
          <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
            {language === 'ar' ? 'يمكنك تحديث معلوماتك الشخصية من هنا' : 'You can update your personal information here'} 
          </Typography>
        </Box>
      </Paper>

      {/* Upgrade to Shop Owner Button */}
      {user?.role !== 'admin' && user?.role !== 'owner' && (
        <Paper sx={{ 
          p: 3, 
          mb: 4,
          backgroundColor: 'rgba(255, 215, 0, 0.05)',
          border: '2px solid #FFD700',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StorefrontIcon sx={{ fontSize: 40, color: '#FFD700' }} />
            <Box>
              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                {language === 'ar' ? 'كن صاحب متجر' : 'Become a store Owner'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' 
                  ? 'قم بتقديم طلب لفتح متجرك الخاص والبدء في بيع منتجاتك' 
                  : 'Submit a request to open your own store and start selling your products'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<StorefrontIcon />}
            onClick={handleUpgradeToShopOwner}
            sx={{
              backgroundColor: '#FFD700',
              color: '#000000',
              fontWeight: 800,
              py: 1.5,
              px: 3,
              borderRadius: '12px',
              border: '1px solid #FFD700',
              '&:hover': {
                backgroundColor: '#FFE55C',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            {language === 'ar' ? 'تقديم طلب' : 'Submit Request'}
          </Button>
        </Paper>
      )}

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ 
            mb: 3,
            backgroundColor: message.type === 'success' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 61, 0, 0.1)',
            color: message.type === 'success' ? '#FFD700' : '#FF3D00',
            border: `1px solid ${message.type === 'success' ? '#FFD700' : '#FF3D00'}`,
            borderRadius: '12px',
            '& .MuiAlert-icon': {
              color: message.type === 'success' ? '#FFD700' : '#FF3D00',
            }
          }}
        >
          {message.text}
        </Alert>
      )}

      {/* Main Form Card */}
      <Paper sx={{ 
        p: 4,
        backgroundColor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '24px',
        boxShadow: '0 8px 24px rgba(255, 215, 0, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          border: '1px solid #FFD700',
          boxShadow: '0 12px 32px rgba(255, 215, 0, 0.2)',
        }
      }}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <Typography variant="h6" sx={{ 
            color: '#FFD700',
            fontWeight: 700,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <AccountCircleIcon sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'المعلومات الشخصية' : 'Personal information'}
          </Typography>

          <Divider sx={{ 
            backgroundColor: '#2D2D2D',
            height: '1px',
            mb: 3
          }} />

          {/* Username */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccountCircleIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                {language === 'ar' ? 'اسم المستخدم' : 'User Name'} 
              </Typography>
            </Box>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'أدخل اسم المستخدم' : 'Enter User Name'} 
              name="username"
              value={formData.username}
              onChange={handleChange('username')}
              onBlur={handleBlur('username')}
              error={touched.username && !!errors.username}
              helperText={touched.username && errors.username}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& fieldset': {
                    borderColor: '#2D2D2D',
                  },
                  '&:hover fieldset': {
                    borderColor: '#FFD700',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#FFD700',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#B3B3B3',
                },
                '& .MuiFormHelperText-root': {
                  color: '#FF3D00',
                },
              }}
            />
          </Box>

          {/* Email - Disabled */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EmailIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                {language === 'ar' ? 'البريد الإلكتروني' : 'E-mail'}
              </Typography>
              {user?.is_google_account && (
                <Chip 
                  icon={<GoogleIcon />}
                  label="Google"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    color: '#FFD700',
                    border: '1px solid #FFD700',
                    height: 24,
                    mr: 1
                  }}
                />
              )}
            </Box>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'البريد الإلكتروني' : 'E-mail'}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
              required
              disabled
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#0D0D0D',
                  color: '#6B6B6B',
                  '& fieldset': {
                    borderColor: '#2D2D2D',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#6B6B6B',
                },
                '& .Mui-disabled': {
                  '-webkit-text-fill-color': '#6B6B6B',
                },
              }}
            />
          </Box>

          {/* Age & Sex Row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
            {/* Age */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CakeIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                  {language === 'ar' ? 'العمر' : 'Age'}
                </Typography>
              </Box>
              <TextField
                fullWidth
                placeholder={language === 'ar' ? 'العمر' : 'Age'}
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange('age')}
                onBlur={handleBlur('age')}
                error={touched.age && !!errors.age}
                helperText={touched.age && errors.age}
                inputProps={{ min: 1, max: 120 }}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#2D2D2D',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FFD700',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#B3B3B3',
                  },
                }}
              />
            </Box>

            {/* Sex */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WcIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                  {language === 'ar' ? 'الجنس' : 'Sex'}
                </Typography>
              </Box>
              <TextField
                select
                fullWidth
                placeholder={language === 'ar' ? 'اختر الجنس' : 'Choose Sex'}
                name="sex"
                value={formData.sex}
                onChange={handleChange('sex')}
                onBlur={handleBlur('sex')}
                error={touched.sex && !!errors.sex}
                helperText={touched.sex && errors.sex}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#1A1A1A',
                    color: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#2D2D2D',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FFD700',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FFD700',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#B3B3B3',
                  },
                  '& .MuiMenuItem-root': {
                    backgroundColor: '#0D0D0D',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#FFD700',
                      color: '#000000',
                    },
                  },
                }}
              >
                <MenuItem value="">{language === 'ar' ? 'اختر نوع الجنس' : 'Choose Sex type'}</MenuItem>
                <MenuItem value="male">{language === 'ar' ? 'ذكر' : 'Male'}</MenuItem>
                <MenuItem value="female">{language === 'ar' ? 'انثى' : 'Female'}</MenuItem>
              </TextField>
            </Box>
          </Box>

          {/* Phone Number with Verification */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PhoneIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
              <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
              </Typography>
              {isPhoneVerified ? (
                <Chip
                  icon={<VerifiedIcon sx={{ fontSize: 16 }} />}
                  label={language === 'ar' ? 'موثق' : 'Verified'}
                  size="small"
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    ml: 1
                  }}
                />
              ) : (
                <Button
                  size="small"
                  onClick={() => setShowVerification(!showVerification)}
                  sx={{
                    color: '#FFD700',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    ml: 1,
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                  }}
                >
                  {language === 'ar' ? 'توثيق' : 'Verify'}
                </Button>
              )}
            </Box>
            
            <PhoneNumberField
              fullWidth
              placeholder={language === 'ar' ? 'أدخل رقم الهاتف' : 'Enter Phone Number'}
              name="user_phone"
              value={formData.user_phone}
              onChange={handleChange('user_phone')}
              onBlur={handleBlur('user_phone')}
              error={touched.user_phone && !!errors.user_phone}
              helperText={touched.user_phone && errors.user_phone}
              lang={language}
              required
              disabled={isPhoneVerified}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: isPhoneVerified ? '#6B6B6B' : '#FFFFFF',
                  '& fieldset': {
                    borderColor: isPhoneVerified ? '#4CAF50' : '#2D2D2D',
                  },
                  '&:hover fieldset': {
                    borderColor: isPhoneVerified ? '#4CAF50' : '#FFD700',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isPhoneVerified ? '#4CAF50' : '#FFD700',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#B3B3B3',
                },
                '& .Mui-disabled': {
                  '-webkit-text-fill-color': '#6B6B6B !important',
                },
              }}
            />
            
            {showVerification && (
              <Box sx={{ mt: 2 }}>
                <PhoneVerification 
                  language={language}
                  onVerified={handlePhoneVerified}
                  currentPhoneNumber={formData.user_phone}
                />
              </Box>
            )}
          </Box>

          {/* Password Section - Only for non-Google accounts */}
          {!user?.is_google_account && (
            <>
              <Typography variant="h6" sx={{ 
                color: '#FFD700',
                fontWeight: 700,
                mb: 2,
                mt: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <LockIcon sx={{ color: '#FFD700' }} /> {language === 'ar' ? 'تغيير كلمة المرور' : 'Change password'}
              </Typography>

              <Divider sx={{ 
                backgroundColor: '#2D2D2D',
                height: '1px',
                mb: 3
              }} />

              {/* Current Password */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LockIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'كلمة المرور الحالية' : 'Current password'}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder={language === 'ar' ? 'أدخل كلمة المرور الحالية' : 'Enter Current password'}
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  onBlur={handleBlur('password')}
                  error={touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#B3B3B3' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#1A1A1A',
                      color: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#2D2D2D',
                      },
                      '&:hover fieldset': {
                        borderColor: '#FFD700',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FFD700',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#B3B3B3',
                    },
                  }}
                />
              </Box>

              {/* New Password */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LockIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'كلمة المرور الجديدة' : 'New password'}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder={language === 'ar' ? 'أدخل كلمة المرور الجديدة' : 'Enter New password'}
                  type={showPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange('newPassword')}
                  onBlur={handleBlur('newPassword')}
                  error={touched.newPassword && !!errors.newPassword}
                  helperText={touched.newPassword && errors.newPassword}
                  InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#B3B3B3' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#1A1A1A',
                      color: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#2D2D2D',
                      },
                      '&:hover fieldset': {
                        borderColor: '#FFD700',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FFD700',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#B3B3B3',
                    },
                  }}
                />
              </Box>

              {/* Confirm Password */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <LockIcon sx={{ fontSize: 20, color: '#B3B3B3' }} />
                  <Typography sx={{ color: '#B3B3B3', fontSize: '0.9rem' }}>
                    {language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm password'}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder={language === 'ar' ? 'أعد إدخال كلمة المرور الجديدة' : 'Confirm new password'}
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  onBlur={handleBlur('confirmPassword')}
                  error={touched.confirmPassword && !!errors.confirmPassword}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#B3B3B3' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#B3B3B3' }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#1A1A1A',
                      color: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#2D2D2D',
                      },
                      '&:hover fieldset': {
                        borderColor: '#FFD700',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FFD700',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#B3B3B3',
                    },
                  }}
                />
              </Box>
            </>
          )}

          {/* Google Account Notice */}
          {user?.is_google_account && (
            <Paper sx={{ 
              p: 2, 
              mb: 3,
              backgroundColor: 'rgba(255, 215, 0, 0.05)',
              border: '1px solid #FFD700',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <GoogleIcon sx={{ color: '#FFD700' }} />
              <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'أنت متصل بحساب Google. لا يمكن تغيير كلمة المرور لحسابات Google.' : 'You are logged into a Google account. Passwords for Google accounts cannot be changed.'}
              </Typography>
            </Paper>
          )}

          {/* Submit Button */}
          {(isPhoneVerified || user?.is_google_account) && (
            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              fullWidth
              startIcon={<SaveIcon />}
              sx={{ 
                mt: 3,
                py: 1.8,
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 800,
                fontSize: '1.1rem',
                borderRadius: '12px',
                border: '1px solid #FFD700',
                '&:hover': {
                  backgroundColor: '#FFE55C',
                  boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {language === 'ar' ? 'تحديث الحساب' : 'Update account'}
            </Button>
          )}

          {/* Show message if phone not verified */}
          {!isPhoneVerified && !user?.is_google_account && formData.user_phone && (
            <Alert 
              severity="warning" 
              sx={{ 
                mt: 3,
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: '#FF9800',
                border: '1px solid #FF9800',
              }}
            >
              {language === 'ar' 
                ? 'يرجى توثيق رقم الهاتف لتتمكن من تحديث الملف الشخصي' 
                : 'Please verify your phone number to update your profile'}
            </Alert>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default UserAccountPage;